import { createElement, Fragment, type ReactNode } from 'react'
import { locales } from './locale'

export function slugify(str: any) {
  if (typeof str !== 'string') return 'undef'
  return str
    .toLowerCase() // Convert the string to lowercase letters
    .trim() // Remove whitespace from both sides of a string
    .replace(/^[a-z]\.\s/g, '') // Remove list form items for terms.  Eg A. Bit of a hack
    .replace(/[']/g, '') // Remove certain non-word chars
    .replace(/[^\p{L}\p{N}]+/gu, '-') // Replace all non-alphanumeric chars with -
    .replace(/--+/g, '-') // Replace multiple - with single -
    .replace(/^-+|-+$/g, '') // Remove leading and trailing hyphens
}

export const cx = (...classNames: Array<string | boolean | undefined | null>) =>
  classNames.filter((cn) => !!cn).join(' ')

export const moneyFormat = (amount: number) => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })

  return formatter.format(amount || 0)
}

export const dateFormat = (
  dateString: string,
  locale: string,
  options = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  } as Partial<Record<string, string>>
) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  if (isNaN(date.getTime())) return ''
  date.setUTCHours(12) // add half day so that it doesn't tz round to the previous day
  return date.toLocaleDateString(locale, options)
}

export const dateTimeFormat = (
  dateString: string,
  locale: string,
  options = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    // timeZone: 'America/New_York',
    timeZoneName: 'short',
  } as Partial<Record<string, string>>
) => {
  const date = new Date(dateString)
  return date.toLocaleTimeString(locale, options)
}

export const dateDiff = (dateString: string, locale: string) => {
  const DIVISIONS = [
    { amount: 60, name: 'seconds' },
    { amount: 60, name: 'minutes' },
    { amount: 24, name: 'hours' },
    { amount: 7, name: 'days' },
    { amount: 4.34524, name: 'weeks' },
    { amount: 12, name: 'months' },
    { amount: Number.POSITIVE_INFINITY, name: 'years' },
  ]

  const date = Number(new Date(dateString))
  let duration = (date - Number(new Date())) / 1000

  for (let i = 0; i <= DIVISIONS.length; i++) {
    const division = DIVISIONS[i]
    if (Math.abs(duration) < division.amount) {
      const formatter = new Intl.RelativeTimeFormat(locale, {
        numeric: 'auto',
      })
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return formatter.format(Math.round(duration), division.name)
    }
    duration /= division.amount
  }
}

export const emailRegex = '^[^\\s@]{1,64}@[^\\s@]{1,64}\\.[^\\s@]{1,64}$'

export function getAltPath(path: string, extension: string) {
  const pathBeforeQuery = path.split(/[?#]/)[0]
  const cleanPath = pathBeforeQuery.replace(/\/+$/, '')

  // Root path becomes /index.md
  if (cleanPath === '') {
    return '/index' + extension
  }

  // Check if path is a locale homepage (e.g., /de-de/)
  const isLocaleHomepage = locales.some((locale) => cleanPath === `/${locale.slug}`)
  if (isLocaleHomepage) {
    return cleanPath + '/index' + extension
  }

  // All other paths: strip trailing slash and add extension
  return cleanPath + extension
}

export const copyHtmlToClipBoard = (str: string) => {
  const plain = str
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<\/ul>/gi, '\n\n')
    .replace(/<li>/gi, ' - ')
    .replace(/<\/li>/gi, '\n')
    .replace(/&nbsp;/gi, ' ')
    .replace(/href="\//gi, 'href="https://bitwarden.com/')
    .replace(/<a[^>]+href="(.*?)"[^>]*>([^<]+)<\/a>/gi, '[$2]($1)')
    .replace(/<[^>]*>?/gm, '')

  const html = str.replace(/href="\//gi, 'href="https://bitwarden.com/')

  const copyRichText = () => {
    navigator.clipboard.write([
      new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([plain], { type: 'text/plain' }),
      }),
    ])
  }

  /**
   * This returns a boolean and is meant to catch browsers that don't
   * support the `execCommand('copy')` function
   */
  const execCommandAvailable = document.execCommand('copy')

  const copyRichTextFallback = () => {
    function listener(e: ClipboardEvent) {
      e.clipboardData?.setData('text/html', html)
      e.clipboardData?.setData('text/plain', plain)
      e.preventDefault()
    }
    document.addEventListener('copy', listener)
    document.execCommand('copy')
    document.removeEventListener('copy', listener)
  }

  const copyPlainText = () => {
    navigator.clipboard.writeText(plain)
  }

  if (window.ClipboardItem) {
    copyRichText()
  } else if (execCommandAvailable) {
    copyRichTextFallback()
  } else {
    copyPlainText()
  }
}

export const capitalize = (str: string) => {
  if (typeof str !== 'string' || str.length === 0) return str
  return str.charAt(0).toUpperCase() + str.slice(1)
}

/** Parses ^sup^ and ~sub~ markers from a plain string into React nodes. */
export const formatSupSub = (input: string): ReactNode => {
  const parts = input.split(/(\^[^^]+\^|~[^~]+~)/g)
  const tokenCounts = new Map<string, number>()

  return parts.map((part) => {
    const count = (tokenCounts.get(part) ?? 0) + 1
    tokenCounts.set(part, count)
    const key = `${part}-${count}`

    if (part.startsWith('^') && part.endsWith('^')) {
      return createElement('sup', { key }, part.slice(1, -1))
    }
    if (part.startsWith('~') && part.endsWith('~')) {
      return createElement('sub', { key }, part.slice(1, -1))
    }
    return createElement(Fragment, { key }, part)
  })
}
